import { useSyncExternalStore } from 'react';

import { logDebug } from './logger';

export const WORKSPACE_PRESENCE_EVENT = 'output:workspace-presence';

const listeners = new Set();
const receivedPayloads = new WeakSet();
const EMPTY_PRESENCE_BY_WORKSPACE_ID = Object.freeze({});

let presenceByWorkspaceId = Object.freeze({});
let windowListenerStarted = false;

function normalizeId(id) {
  if (id === null || id === undefined || id === '') return null;
  return String(id);
}

function normalizeVersion(version) {
  const normalized = Number(version);
  return Number.isFinite(normalized) ? normalized : null;
}

function recordsFor(presence) {
  return Array.isArray(presence?.records) ? presence.records : [];
}

function participantKey(participant) {
  const type = normalizeId(participant?.participant_type);
  const id = normalizeId(participant?.participant_id);
  return type && id ? `${type}:${id}` : null;
}

function normalizeParticipant(participant) {
  const key = participantKey(participant);
  if (!key) return null;

  return Object.freeze({
    ...participant,
    participant_type: normalizeId(participant.participant_type),
    participant_id: normalizeId(participant.participant_id),
    profile_id: normalizeId(participant.profile_id),
  });
}

function normalizePresence(presence) {
  const workspaceId = normalizeId(presence?.workspace_id);
  if (!workspaceId) return null;

  const records = Object.freeze(recordsFor(presence).map(normalizeParticipant).filter(Boolean));
  const version = normalizeVersion(presence?.version);

  const normalized = {
    ...presence,
    workspace_id: workspaceId,
    records,
    total: records.length,
  };
  if (version !== null) normalized.version = version;

  return Object.freeze(normalized);
}

function normalizeMessage(payload) {
  const action = payload?.action;
  const data = payload?.data || payload;
  return action && data ? { action, data } : null;
}

function isStaleWorkspaceEvent(previousPresence, version) {
  const previousVersion = normalizeVersion(previousPresence?.version);
  const nextVersion = normalizeVersion(version);
  return previousVersion !== null && (nextVersion === null || nextVersion < previousVersion);
}

function presenceContentEqual(a, b) {
  if (!a || !b) return false;
  if (a.version !== b.version) return false;
  if (a.total !== b.total) return false;
  const aRecords = recordsFor(a);
  const bRecords = recordsFor(b);
  if (aRecords.length !== bRecords.length) return false;
  for (let i = 0; i < aRecords.length; i += 1) {
    if (participantKey(aRecords[i]) !== participantKey(bRecords[i])) return false;
    if (aRecords[i].session_count !== bRecords[i].session_count) return false;
    if (aRecords[i].last_seen_at !== bRecords[i].last_seen_at) return false;
  }
  return true;
}

function notify() {
  listeners.forEach((listener) => listener());
}

function storePresenceState(presence) {
  const normalizedPresence = normalizePresence(presence);
  if (!normalizedPresence) return;

  const workspaceId = normalizedPresence.workspace_id;
  if (presenceContentEqual(presenceByWorkspaceId[workspaceId], normalizedPresence)) return;

  // Snapshots are authoritative — always replace, even when version regresses.
  // The server's per-workspace version key has a TTL; an idle workspace can reset
  // the counter, and a stale-version guard here would wedge clients permanently.
  presenceByWorkspaceId = Object.freeze({
    ...presenceByWorkspaceId,
    [workspaceId]: normalizedPresence,
  });
  notify();
}

function storeParticipantDelta({ workspaceId, participant, version, remove = false }) {
  const normalizedWorkspaceId = normalizeId(workspaceId);
  const normalizedParticipant = normalizeParticipant(participant);
  const key = participantKey(normalizedParticipant);
  if (!normalizedWorkspaceId || !key) return;

  const previousPresence = presenceByWorkspaceId[normalizedWorkspaceId] || null;
  if (isStaleWorkspaceEvent(previousPresence, version)) {
    logDebug('workspace-presence', 'delta ignored: stale version', {
      workspaceId: normalizedWorkspaceId,
      version,
      previousVersion: previousPresence?.version,
    });
    return;
  }

  const previousRecords = recordsFor(previousPresence);
  const nextRecords = Object.freeze(
    remove
      ? previousRecords.filter((record) => participantKey(record) !== key)
      : [...previousRecords.filter((record) => participantKey(record) !== key), normalizedParticipant].sort((a, b) =>
          participantKey(a).localeCompare(participantKey(b)),
        ),
  );

  presenceByWorkspaceId = Object.freeze({
    ...presenceByWorkspaceId,
    [normalizedWorkspaceId]: Object.freeze({
      workspace_id: normalizedWorkspaceId,
      ...(previousPresence || {}),
      records: nextRecords,
      total: nextRecords.length,
      version: normalizeVersion(version) ?? normalizeVersion(previousPresence?.version) ?? 0,
    }),
  });
  notify();
}

export function receiveWorkspacePresencePayload(payload) {
  if (payload && typeof payload === 'object') receivedPayloads.add(payload);

  const message = normalizeMessage(payload);
  if (!message) return;

  switch (message.action) {
    case 'presence_state':
      storePresenceState(message.data.presence);
      break;
    case 'presence_join':
    case 'presence_update':
      storeParticipantDelta({
        workspaceId: message.data.workspace_id,
        participant: message.data.participant,
        version: message.data.version,
      });
      break;
    case 'presence_leave':
      storeParticipantDelta({
        workspaceId: message.data.workspace_id,
        participant: message.data.participant,
        version: message.data.version,
        remove: true,
      });
      break;
    default:
      logDebug('workspace-presence', 'receive ignored: unhandled action', { action: message.action });
  }
}

export function publishWorkspacePresencePayload(payload) {
  receiveWorkspacePresencePayload(payload);
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(WORKSPACE_PRESENCE_EVENT, { detail: payload }));
}

export function removeWorkspacePresence(workspaceId) {
  const normalizedWorkspaceId = normalizeId(workspaceId);
  if (!normalizedWorkspaceId || !(normalizedWorkspaceId in presenceByWorkspaceId)) return;

  const next = { ...presenceByWorkspaceId };
  delete next[normalizedWorkspaceId];
  presenceByWorkspaceId = Object.freeze(next);
  notify();
}

function handlePresenceEvent(event) {
  const payload = event?.detail;
  if (payload && typeof payload === 'object' && receivedPayloads.has(payload)) return;

  receiveWorkspacePresencePayload(payload);
}

function ensureWindowListener() {
  if (windowListenerStarted || typeof window === 'undefined') return;

  windowListenerStarted = true;
  window.addEventListener(WORKSPACE_PRESENCE_EVENT, handlePresenceEvent);
}

export function subscribeWorkspacePresence(listener) {
  ensureWindowListener();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getWorkspacePresenceByWorkspaceId() {
  return presenceByWorkspaceId;
}

export function getProfileWorkspacePresenceStatus(workspaceId, profileId) {
  const normalizedWorkspaceId = normalizeId(workspaceId);
  const normalizedProfileId = normalizeId(profileId);
  if (!normalizedWorkspaceId || !normalizedProfileId) return null;

  const presence = presenceByWorkspaceId[normalizedWorkspaceId];
  if (!presence) return null;

  return recordsFor(presence).some((record) => record?.participant_type === 'profile' && normalizeId(record.profile_id) === normalizedProfileId);
}

export function useWorkspacePresenceByWorkspaceId() {
  return useSyncExternalStore(
    subscribeWorkspacePresence,
    getWorkspacePresenceByWorkspaceId,
    () => EMPTY_PRESENCE_BY_WORKSPACE_ID,
  );
}

export function useProfileWorkspacePresenceStatus(workspaceId, profileId) {
  return useSyncExternalStore(
    subscribeWorkspacePresence,
    () => getProfileWorkspacePresenceStatus(workspaceId, profileId),
    () => null,
  );
}
